<adapt-dialog [maxWidth]="dialogWidth"
              [expandable]="true"
              [fitHeight]="true"
              (fullscreenChange)="onFullscreenToggled($event)"
              (shown)="updateDimensions()">
    <div adapt-dialog-title
         class="d-flex flex-row flex-grow-1 align-items-center">
        @if (currentWorkflowStep && data.workflow.showProgress) {
            <div class="flex-grow-1">
                <adapt-stepped-progress-bar class="progress-bar"
                                            [steps]="progressSteps"
                                            [disabled]="isBusy"
                                            [labelOnly]="false"
                                            [showTitles]="data.workflow.showProgressTitles"
                                            [currentStepIdx]="currentWorkflowStep.ordinal"
                                            data-test="workflow-progress-bar"></adapt-stepped-progress-bar>
            </div>
        } @else {
            @if (!currentWorkflowStep?.hideTitle && dialogTitle; as title) {
                <h3 data-test="workflow-run-dialog-title">
                    @if (data.workflow.iconStyle) {
                        <i class="me-3"
                           [ngClass]="[data.workflow.iconStyle]"></i>
                    }
                    {{ title }}
                </h3>
            }
        }
    </div>

    <ng-container adapt-dialog-menu>
        @if (stepLocation; as location) {
            <span class="step-count me-3 d-none d-sm-inline">{{ location }}</span>
        }

        @if (currentWorkflowStep?.titleRightButtons) {
            @for (buttonDef of currentWorkflowStep!.titleRightButtons; track buttonDef) {
                <button class="btn btn-primary btn-pill"
                        [adaptButtonIcon]="buttonDef.icon"
                        (click)="handleButtonClick(buttonDef.onClick)">
                    {{ buttonDef.buttonText }}
                </button>
            }
        }

        @if (currentWorkflowStep && !currentWorkflowStep.isConfirmationStep) {
            <button type="button"
                    data-test="workflow-run-dialog-close"
                    class="ms-2 btn-close order-last"
                    (click)="data.dismissDialog ? showDismissDialog() : cancel()"
                    aria-label="Close"></button>
        }
    </ng-container>

    <div adapt-dialog-content
         class="h-100 d-flex flex-column">
        <div class="workflow-step-content">
            @if (isCompleted && !!wrapUpSlug) {
                <div class="wrap-up-content">
                    <h1 class="wrap-up-heading text-center pt-3 mb-0">
                        <i class="fal fa-circle-check fa-4x"></i>
                    </h1>
                    <adapt-styled-implementation-kit [attr.data-test]="'workflow-run-wrapup-slug-' + wrapUpSlug"
                                                     [articleId]="wrapUpSlug"
                                                     contentStyle="embedded"></adapt-styled-implementation-kit>
                </div>
                <div class="my-auto step-component">
                    <h3>Please rate this pathway</h3>
                    <div>
                        <adapt-rating-stars [(rating)]="workflowRating.rating"></adapt-rating-stars>
                    </div>
                    <div class="form-group">
                        <label>What did you like about this pathway?</label>
                        <dx-text-area [(value)]="workflowRating.liked"></dx-text-area>
                    </div>
                    <div class="form-group">
                        <label>How could we improve this pathway?</label>
                        <dx-text-area [(value)]="workflowRating.improveOn"></dx-text-area>
                    </div>
                </div>
            }

            @if (currentWorkflowStep && !isCompleted) {
                @if (currentWorkflowStep!.isOutcomeStep || currentWorkflowStep!.isActivityStep) {
                    <div>
                        <adapt-display-workflow-outcome-header [style]="currentWorkflowStep!.isOutcomeStep ? 'burgundy' : 'wise-lime'"
                                                               [small]="true"
                                                               data-test="workflow-dialog-outcome"
                                                               [workflow]="currentWorkflowStep!.workflow.extensions.rootWorkflow"></adapt-display-workflow-outcome-header>
                        @if (currentWorkflowStep!.articleSlug) {
                            <div class="mt-3"
                                 data-test="workflow-dialog-article-slug">
                                <adapt-implementation-kit-content [attr.data-test]="'workflow-run-article-slug-' + currentWorkflowStep!.articleSlug"
                                                                  [articleId]="currentWorkflowStep!.articleSlug"></adapt-implementation-kit-content>
                            </div>
                        }
                    </div>
                } @else {
                    @if (currentWorkflowStep!.articleSlug || currentWorkflowStep!.simpleHtml) {
                        <div [adaptCollapsible]="currentWorkflowStep!.articleSlug"
                             [adaptCollapsibleVertical]="currentWorkflowStep!.articlePosition === WorkflowStepGuidancePosition.Top"
                             (adaptCollapsibleExpanded)="updateDimensions()"
                             class="implementation-kit"
                             [ngClass]="{
                                'implementation-kit-top': currentWorkflowStep!.articlePosition === WorkflowStepGuidancePosition.Top,
                                'not-full-width': !fullWidthImplementationKit && currentWorkflowStep!.articlePosition !== WorkflowStepGuidancePosition.Top,
                            }">
                            <dx-scroll-view #implementationKitScrollView
                                            [height]="isMobile ? 'auto' : undefined"
                                            showScrollbar="always">
                                @if (currentWorkflowStep!.articleSlug) {
                                    <adapt-styled-implementation-kit [articleId]="currentWorkflowStep!.articleSlug"
                                                                     [contentStyle]="'embedded'"
                                                                     [attr.data-test]="'workflow-run-scroll-article-slug-' + currentWorkflowStep!.articleSlug"
                                                                     (contentLoaded)="updateDimensions()"></adapt-styled-implementation-kit>
                                } @else {
                                    <div class="embedded-content"
                                         [froalaView]="currentWorkflowStep!.simpleHtml"
                                         (contentLoaded)="updateDimensions()"></div>
                                }
                            </dx-scroll-view>
                        </div>
                    }
                }
                @if (currentWorkflowStep!.componentSelector || currentWorkflowStep!.deepDiveId || errorMessage) {
                    <div class="step-component"
                         [ngClass]="{
                            'overflow-hidden': !!currentWorkflowStep!.componentMinWidth,
                            'not-full-width': !fullWidthImplementationKit
                                && currentWorkflowStep!.articlePosition !== WorkflowStepGuidancePosition.Top
                                && !(currentWorkflowStep!.isOutcomeStep || currentWorkflowStep!.isActivityStep),
                        }"
                         [style.min-width]="currentWorkflowStep!.componentMinWidth">
                        <dx-scroll-view #stepComponentScrollView
                                        [height]="isMobile ? 'auto' : undefined"
                                        showScrollbar="always">
                            @if (currentWorkflowStep!.componentSelector) {
                                <ng-container #container></ng-container>
                            }
                            @if (currentWorkflowStep!.deepDiveId) {
                                <adapt-deep-dive [deepDiveId]="currentWorkflowStep!.deepDiveId"
                                                 (opened)="currentStepCompleted = true"></adapt-deep-dive>
                            }
                            @if (errorMessage) {
                                <div class="error-message alert alert-danger">
                                    {{ errorMessage }}
                                </div>
                            }
                        </dx-scroll-view>
                    </div>
                }
            }
        </div>
    </div>

    @if (currentWorkflowStep) {
        <div adapt-dialog-footer
             class="workflow-step-footer">
            @if (!isCompleted) {
                <div class="d-flex gap-1 me-auto">
                    @if (!isFirstStep && currentWorkflowStep!.allowBack && !hideBackButton) {
                        <button class="btn btn-outline-primary"
                                data-test="workflow-run-dialog-back"
                                [disabled]="!currentWorkflowStep!.allowBack || isBusy"
                                [adaptBlockingClick]="blockingPrevious">
                            <i class="fal fa-fw fa-chevron-left me-2"></i>
                            @if (currentWorkflowStep.backButtonText) {
                                {{ currentWorkflowStep.backButtonText }}
                            } @else {
                                Back
                                @if (previousWorkflowStep; as prevStep) {
                                    <span class="d-none d-sm-inline"> - {{ prevStep.name }}</span>
                                }
                            }
                        </button>
                    }
                    @if (footerLeft; as items) {
                        @for (item of items; track item.template) {
                            <ng-container [ngTemplateOutlet]="item.template"></ng-container>
                        }
                    }
                </div>
                <div class="d-flex gap-1 mx-auto flex-grow-1">
                    @if (footerCenter; as items) {
                        @for (item of items; track item.template) {
                            <ng-container [ngTemplateOutlet]="item.template"></ng-container>
                        }
                    }
                </div>
                <div class="d-flex gap-1 ms-auto">
                    @if (footerRight; as items) {
                        @for (item of items; track item.template) {
                            <ng-container [ngTemplateOutlet]="item.template"></ng-container>
                        }
                    }
                    @if (!isLastStep && currentWorkflowStep!.canSkip && !currentStepCompleted) {
                        <button class="btn btn-outline-secondary border-0"
                                [disabled]="isBusy || !currentStepSkippable"
                                (click)="skip()"
                                data-test="workflow-skip">
                            Skip
                            <i class="fal fa-fw fa-arrow-right-to-line ms-2"></i>
                        </button>
                    }
                    @if (currentWorkflowStep?.showCancelButton) {
                        <button class="btn btn-secondary"
                                (click)="cancel()">
                            {{ currentWorkflowStep.cancelButtonText ?? "Close" }}
                        </button>
                    }
                    @if (!isLastStep) {
                        <button [ngClass]="nextStepClass"
                                [disabled]="!currentStepCompleted || isBusy"
                                [adaptBlockingClick]="blockingNext"
                                data-test="workflow-next">
                            {{ nextStepText }}
                            @if (!currentWorkflowStep!.closeAfterNext) {
                                <i class="ms-2"
                                   [ngClass]="currentWorkflowStep!.workflowStepNextIcon ?? 'fal fa-fw fa-chevron-right'"></i>
                            }
                        </button>
                    }
                    @if (isLastStep) {
                        <button [ngClass]="nextStepClass"
                                [disabled]="(!currentStepCompleted && !currentWorkflowStep!.canSkip) || isBusy"
                                [adaptBlockingClick]="complete"
                                [unsubscribeOnDestroy]="false"
                                data-test="workflow-close">
                            {{ closeText }}
                            @if (currentWorkflowStep!.workflowStepNextIcon; as icon) {
                                <i class="ms-2"
                                   [ngClass]="icon"></i>
                            }
                        </button>
                    }
                </div>
            } @else {
                <div class="d-flex ms-auto gap-3">
                    @if (!!wrapUpTour && !isMobile) {
                        <button class="btn btn-primary"
                                (click)="runWrapUpTour()">{{ canSubmitRating() ? "Send feedback & " : "" }}Start tour</button>
                    }
                    @if (!wrapUpTour || isMobile) {
                        <button class="btn btn-primary"
                                (click)="closeDialog()"
                                data-test="workflow-close">{{ canSubmitRating() ? "Send feedback & " : "" }}Close</button>
                    }
                </div>
            }
        </div>
    }
</adapt-dialog>
